import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { S3ImportJob, S3Source } from '../api/types';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatEUR, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState, PendingState } from '../components/States';
import { Button, Card, CardHead, Spinner, Stat } from '../components/ui';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { IconCloud, IconArchive, IconCheck } from '../components/Icons';

export function S3Import() {
  const { t } = useTranslation();
  const sources = useAsync(() => api.s3Sources(), []);

  return (
    <>
      <PageHeader title={t('s3import.title')} subtitle={t('s3import.subtitle')} />
      <ApiState {...sources} onRetry={sources.reload}>
        {(data) => {
          const items: S3Source[] = 'items' in data && Array.isArray(data.items) ? data.items : [];
          return <S3Body sources={items} />;
        }}
      </ApiState>
    </>
  );
}

function S3Body({ sources }: { sources: S3Source[] }) {
  // The import size is unknown before it runs, so quote the RATE rather than
  // inventing a total — an honest estimate beats a fabricated one.
  const pricing = useAsync(() => api.pricing(), []);
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [endpoint, setEndpoint] = useState('');
  const [bucket, setBucket] = useState('');
  const [prefix, setPrefix] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [region, setRegion] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<S3ImportJob | null>(null);

  const fillFrom = (s: S3Source) => {
    setEndpoint(s.endpoint);
    setBucket(s.bucket);
    setPrefix(s.prefix ?? '');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setPending(false);
    setResult(null);
    try {
      const job = await api.s3Import({ endpoint, bucket, prefix, accessKey, secretKey, region });
      setResult(job);
      toast.success(t('s3import.started', { id: job.id }));
    } catch (err) {
      if (err instanceof ApiError && err.isNotImplemented) setPending(true);
      else toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <Card>
        <PendingState />
      </Card>
    );
  }

  return (
    <div className="stack">
    {result ? (
      <Card>
        <CardHead title={t('s3import.doneTitle')} />
        <div className="card__body">
          <div className="alert alert--info" style={{ marginTop: 0 }}>
            <IconCheck width={18} height={18} />
            <span>{t('s3import.doneInto', { bucket: result.bucket })}</span>
          </div>
          <div className="grid grid--stats" style={{ marginTop: 14 }}>
            <Stat label={t('s3import.imported')} value={formatNumber(result.imported ?? 0, lang)} icon={<IconCloud width={16} height={16} />} />
            <Stat label={t('common.size')} value={formatBytes(result.bytes ?? 0, lang)} />
          </div>
          {result.archiveId ? (
            <Link className="btn btn--primary" to={`/archives/${result.archiveId}`} style={{ marginTop: 16 }}>
              <IconArchive width={18} height={18} />
              {t('s3import.openArchive')}
            </Link>
          ) : null}
        </div>
      </Card>
    ) : null}
    <div className="grid grid--2">
      <Card>
        <CardHead title={t('s3import.sources')} />
        {sources.length === 0 ? (
          <EmptyState title={t('s3import.noSources')} icon={<IconCloud width={26} height={26} />} />
        ) : (
          <div className="card__body stack" style={{ gap: 8 }}>
            {sources.map((s) => (
              <button key={`${s.endpoint}-${s.bucket}`} type="button" className="btn btn--secondary" style={{ justifyContent: 'flex-start' }} onClick={() => fillFrom(s)}>
                <IconCloud width={18} height={18} />
                <span style={{ textAlign: 'left' }}>
                  <strong>{s.name || s.bucket}</strong>
                  <br />
                  <span className="muted mono" style={{ fontSize: 12 }}>
                    {s.endpoint}/{s.bucket}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHead title={t('s3import.start')}>
          {pricing.data?.hasPlan ? (
            <span className="muted" style={{ fontSize: 13 }}>
              {t('s3import.rate', {
                rate: formatEUR(pricing.data.writeEurPerGb, lang),
                min: formatBytes(pricing.data.minBillableObjectBytes, lang),
              })}
            </span>
          ) : null}
        </CardHead>
        <form className="card__body" onSubmit={submit}>
          <Field label={t('s3import.endpoint')} required>
            {(p) => (
              <input className="input" {...p} value={endpoint} required placeholder={t('s3import.endpointPlaceholder')} onChange={(e) => setEndpoint(e.target.value)} />
            )}
          </Field>
          <Field label={t('s3import.bucket')} required>
            {(p) => (
              <input className="input" {...p} value={bucket} required placeholder={t('s3import.bucketPlaceholder')} onChange={(e) => setBucket(e.target.value)} />
            )}
          </Field>
          <Field label={t('s3import.prefix')} hint={t('common.optional')}>
            {(p) => <input className="input" {...p} value={prefix} placeholder={t('s3import.prefixPlaceholder')} onChange={(e) => setPrefix(e.target.value)} />}
          </Field>
          <Field label={t('s3import.accessKey')}>
            {(p) => (
              <input className="input mono" {...p} value={accessKey} autoComplete="off" placeholder={t('s3import.accessKeyPlaceholder')} onChange={(e) => setAccessKey(e.target.value)} />
            )}
          </Field>
          <Field label={t('s3import.secretKey')}>
            {(p) => (
              <input className="input mono" {...p} type="password" value={secretKey} autoComplete="new-password" placeholder={t('s3import.secretKeyPlaceholder')} onChange={(e) => setSecretKey(e.target.value)} />
            )}
          </Field>
          <Field label={t('s3import.region')} hint={t('common.optional')}>
            {(p) => <input className="input mono" {...p} value={region} placeholder="us-east-1" onChange={(e) => setRegion(e.target.value)} />}
          </Field>
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>{t('s3import.credHint')}</p>
          <Button type="submit" variant="primary" disabled={busy || !endpoint || !bucket}>
            {busy ? <Spinner /> : <IconCloud width={18} height={18} />}
            {t('s3import.start')}
          </Button>
        </form>
      </Card>
    </div>
    </div>
  );
}
